import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { ConceptoPlanilla } from '../types/concepto';
import { ConceptoService } from '../services';

interface ConceptoState {
  conceptos: ConceptoPlanilla[];
  loading: boolean;
  error: string | null;
  selectedConcepto: ConceptoPlanilla | null;
  dialogOpen: boolean;
  filterTipo: string | null;

  fetchConceptos: () => Promise<void>;
  saveConcepto: (concepto: ConceptoPlanilla) => Promise<void>;
  deleteConcepto: (id: number) => Promise<void>;
  setSelectedConcepto: (concepto: ConceptoPlanilla | null) => void;
  setFilterTipo: (tipo: string | null) => void;
  openDialog: () => void;
  closeDialog: () => void;
  clearError: () => void;
}

export const useConceptoStore = create<ConceptoState>()(
  devtools(
    (set) => ({
      conceptos: [],
      loading: false,
      error: null,
      selectedConcepto: null,
      dialogOpen: false,
      filterTipo: null,

      fetchConceptos: async () => {
        set({ loading: true, error: null }, false, 'conceptos/fetch/start');
        try {
          const conceptos = await ConceptoService.getAll();
          set({ conceptos, loading: false }, false, 'conceptos/fetch/success');
        } catch (error) {
          set(
            { loading: false, error: error instanceof Error ? error.message : 'Error al cargar conceptos' },
            false,
            'conceptos/fetch/error'
          );
        }
      },

      saveConcepto: async (concepto: ConceptoPlanilla) => {
        set({ loading: true, error: null }, false, 'conceptos/save/start');
        try {
          const saved = await ConceptoService.save(concepto);
          const isUpdate = concepto.idConcepto > 0;
          set(
            (state) => ({
              conceptos: isUpdate
                ? state.conceptos.map((c) => c.idConcepto === saved.idConcepto ? saved : c)
                : [...state.conceptos, saved],
              loading: false,
              dialogOpen: false,
              selectedConcepto: null,
            }),
            false,
            isUpdate ? 'conceptos/update/success' : 'conceptos/create/success'
          );
        } catch (error: unknown) {
          const errorObj = error as Record<string, unknown>;
          const errorMessage =
            (errorObj?.userMessage as string) ||
            (errorObj?.message as string) ||
            (error instanceof Error ? error.message : 'Error al guardar concepto');
          set(
            { loading: false, error: errorMessage },
            false,
            'conceptos/save/error'
          );
          throw error;
        }
      },

      deleteConcepto: async (id: number) => {
        set({ loading: true, error: null }, false, 'conceptos/delete/start');
        try {
          await ConceptoService.delete(id);
          set(
            (state) => ({
              conceptos: state.conceptos.filter((c) => c.idConcepto !== id),
              loading: false,
            }),
            false,
            'conceptos/delete/success'
          );
        } catch (error: unknown) {
          const errorObj = error as Record<string, unknown>;
          const errorMessage =
            (errorObj?.userMessage as string) ||
            (errorObj?.message as string) ||
            (error instanceof Error ? error.message : 'Error al eliminar concepto');
          set(
            { loading: false, error: errorMessage },
            false,
            'conceptos/delete/error'
          );
        }
      },

      setSelectedConcepto: (concepto) => {
        set({ selectedConcepto: concepto }, false, 'conceptos/setSelected');
      },

      setFilterTipo: (tipo) => {
        set({ filterTipo: tipo }, false, 'conceptos/setFilterTipo');
      },

      openDialog: () => {
        set({ dialogOpen: true }, false, 'conceptos/openDialog');
      },

      closeDialog: () => {
        set({ dialogOpen: false, selectedConcepto: null }, false, 'conceptos/closeDialog');
      },

      clearError: () => {
        set({ error: null }, false, 'conceptos/clearError');
      },
    }),
    { name: 'concepto-store' }
  )
);
